import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import classNames from 'classnames';
import styled from 'styled-components';

import { getAICategoryRankings } from 'apis/RecommendationsAPI';
import RoomTypeFilter from 'common/Filters/RoomTypeFilter';
import { WIDGET_FILTER_OPTIONS, WIDGET_FILTER_TYPES } from 'common/Widgets/constants';
import MarketPositioningWidgetContent from 'common/Widgets/MarketPositioning/MarketPositioningWidgetContent';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { AGGREGATION_TYPE } from 'components/View/constants';
import { useAppSelector } from 'store/hook';

import AiCiSectionIcon from './AiCiSectionIcon';

const Container = styled.div``;

const Title = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  align-items: center;
  font-size: 20px;
  font-family: var(--font-bold);
  margin-bottom: 10px;
`;

const Filters = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  margin-bottom: 15px;
`;

const ChartArea = styled.div`
  height: 480px;
`;

const MarketPositionGraph = () => {
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const [careTypes, setCareTypes] = useState<number[]>([]);
  const [roomTypes, setRoomTypes] = useState<number[]>([]);
  const [roomTypesPriceDisplay, setRoomTypesPriceDisplay] = useState<number>(AGGREGATION_TYPE.AVG);

  const careTypeOptions = WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.CARE_TYPE].map((item) => ({
    label: item.name,
    value: item.id,
  }));

  const communityId = selectedCommunity?.id ?? 0;

  const { data: overallRankings } = useQuery({
    queryKey: ['ai-ci-category-rankings', communityId, 'overall'],
    queryFn: () => getAICategoryRankings(communityId, 'overall'),
    enabled: communityId > 0,
    refetchOnWindowFocus: false,
  });

  const scoresByCommunityName = useMemo(() => {
    const map: Record<string, number> = {};
    (overallRankings?.rows ?? []).forEach((row) => {
      if (row.community && typeof row.score === 'number') {
        map[row.community] = row.score;
      }
    });
    return map;
  }, [overallRankings]);

  return (
    <Container>
      <Title>
        <AiCiSectionIcon src="/icons/grain.svg" />
        <div>Overall Score vs. Market Rate</div>
      </Title>
      <Filters>
        <SelectAdvancedFilter
          className={classNames({ active: careTypes.length > 0 })}
          placeholder="Care Type"
          options={careTypeOptions}
          defaultValue={careTypes}
          onSelect={(values: any[]) => setCareTypes(values.map((item) => item.value))}
          dataTestId="market-position-care-type-filter"
        />
        <RoomTypeFilter
          roomTypes={roomTypes}
          priceDisplay={roomTypesPriceDisplay}
          onApply={({ roomTypes: nextRoomTypes, roomTypesPriceDisplay: nextPriceDisplay }) => {
            setRoomTypes(nextRoomTypes);
            setRoomTypesPriceDisplay(nextPriceDisplay);
          }}
        />
      </Filters>
      <ChartArea>
        <MarketPositioningWidgetContent
          communityId={communityId}
          filters={{ careTypes, roomTypes, roomTypesPriceDisplay }}
          xAxis={{
            title: 'Score',
            getValue: (point) => scoresByCommunityName[point.community_name],
          }}
          useScoreFormat
        />
      </ChartArea>
    </Container>
  );
};

export default MarketPositionGraph;
